// @ts-nocheck
import React, { useState } from 'react';

import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import { LINE_ITEM_TYPES } from 'dpl/shared/constants/breeder_app';
import { isMobileUA } from 'dpl/shared/utils';
import { sendAnalyticsEvent } from 'dpl/util/analytics';

import { PAYMENT_TIER_TYPE, SELECT_PAYMENT_MODAL_NAME } from './constants';
import SelectPaymentTypeParentModal from './SelectPaymentTypeParentModal';
import StripeChargeModalPaymentSummaryLineItemV2 from './StripeChargeModalPaymentSummaryLineItemV2';

const IS_MOBILE = isMobileUA();

type PaymentTierType =
  (typeof PAYMENT_TIER_TYPE)[keyof typeof PAYMENT_TIER_TYPE];
type SelectPaymentModalName =
  (typeof SELECT_PAYMENT_MODAL_NAME)[keyof typeof SELECT_PAYMENT_MODAL_NAME];

interface FeeBundle {
  amount: string;
  description: string;
  long_description: string;
  tooltip_description: string;
  item_types: string[];
  processing_savings?: string | null;
}

interface TooltipContentProps {
  hasProtectionFee: boolean;
  tooltipDescription: string;
  onClick: () => void;
  description?: string | null;
}

function TooltipContent({
  hasProtectionFee,
  description = null,
  tooltipDescription,
  onClick
}: TooltipContentProps) {
  return (
    <div className="TooltipContent flex flex-column h-100 overflow-hidden">
      {description && (
        <div className="bb b--light-gray pv2 ph3 mb5">
          <h4 className="font-20 tc truncate pr10">{description}</h4>
        </div>
      )}
      <div className="flex flex-column gap-8 font-14 tl pv2-md ph3 ph0-md">
        <p>{tooltipDescription}</p>
        {hasProtectionFee && (
          <button className="underline" type="button" onClick={onClick}>
            Learn more
          </button>
        )}
      </div>
    </div>
  );
}

interface StripeChargeModalFeeBundleLineItemProps {
  feeBundle: FeeBundle;
  paymentTier: PaymentTierType;
  dogPayment: boolean;
  onTierChange: (tier: PaymentTierType) => void;
  subtotalInDollars?: string | number | null;
  disableInteractions?: boolean;
  paymentMethod?: string | null;
}

export default function StripeChargeModalFeeBundleLineItem({
  feeBundle,
  paymentTier,
  dogPayment,
  subtotalInDollars = null,
  onTierChange,
  disableInteractions = false,
  paymentMethod = null
}: StripeChargeModalFeeBundleLineItemProps) {
  const [isProtectionSupportOpen, setIsProtectionSupportOpen] = useState(false);
  const [currentModal, setCurrentModal] =
    useState<SelectPaymentModalName | null>(null);

  function handleOpenModalClick(modalName: SelectPaymentModalName): void {
    setCurrentModal(modalName);

    if (IS_MOBILE) {
      setIsProtectionSupportOpen(false);
    }
  }

  function handleIconClick(): void {
    if (IS_MOBILE) {
      setIsProtectionSupportOpen(true);
    } else {
      setCurrentModal(SELECT_PAYMENT_MODAL_NAME.LEARN_MORE);
    }

    sendAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_MODAL_VIEWED_BY_BUYER, {
      view: ANALYTICS_VIEWS.PROTECTION_SUPPORT_QUICK_VIEW,
      view_feature: ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE
    });
  }

  function handleClosePaymentTierClick(): void {
    setCurrentModal(null);
  }

  function handleSavePaymentTierClick(tier: PaymentTierType): void {
    handleClosePaymentTierClick();
    setIsProtectionSupportOpen(false);
    onTierChange(tier);
  }

  function handleTooltipContentClick(): void {
    handleOpenModalClick(SELECT_PAYMENT_MODAL_NAME.LEARN_MORE);
  }

  const hasProtectionFee = feeBundle.item_types.includes(LINE_ITEM_TYPES.FEE);

  const amountForModal =
    typeof subtotalInDollars === 'number'
      ? subtotalInDollars
      : Number(subtotalInDollars) || 0;

  return (
    <>
      <StripeChargeModalPaymentSummaryLineItemV2
        itemType="fee_bundle"
        amountInDollars={feeBundle.amount}
        className="StripeChargeModalFeeBundleLineItem"
        description={feeBundle.description}
        longDescription={feeBundle.long_description}
        disableInteractions={disableInteractions}
        iconProps={{
          name: 'fetch-info',
          buttonId: 'platform-fee-button'
        }}
        tooltipProps={{
          title: (
            <TooltipContent
              description={feeBundle.description}
              tooltipDescription={feeBundle.tooltip_description}
              hasProtectionFee={hasProtectionFee}
              onClick={handleTooltipContentClick}
            />
          ),
          isVisible: isProtectionSupportOpen,
          onClick: handleIconClick,
          onCloseTooltipClick: () => setIsProtectionSupportOpen(false)
        }}
        modalProps={{ isVisible: false }}
        desktopTooltipProps={{
          title: (
            <TooltipContent
              tooltipDescription={feeBundle.tooltip_description}
              hasProtectionFee={hasProtectionFee}
              onClick={handleTooltipContentClick}
            />
          )
        }}
      />
      {currentModal && (
        <SelectPaymentTypeParentModal
          paymentTier={paymentTier}
          defaultModalName={currentModal}
          onCloseClick={handleClosePaymentTierClick}
          dogPayment={dogPayment}
          amount={amountForModal}
          onSaveSelection={handleSavePaymentTierClick}
          paymentMethod={paymentMethod}
          processingSavings={feeBundle.processing_savings}
        />
      )}
    </>
  );
}
